import { Form, Input, Radio, Row, message } from 'antd';
import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  FormItemInputNumber,
  FormItemSelect,
} from '../../../../components/index';
import {
  CargoSuppressionInternalCreatePayload,
  CargoSuppressionInternalPatchPayload,
  CargoSuppressionItemType,
  CargoSuppressionPrefillContainerOption,
  CargoSuppressionPrefillProductOption,
  useCreateCargoSuppressionInternalMutation,
  useUpdateCargoSuppressionInternalMutation,
} from '../../../../services/cargoSuppressionApi';

const MOTIVO_MIN = 20;
const MOTIVO_MAX = 200;

function formatPesoForBackend(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return null;
  return n.toFixed(3);
}

type CargoSuppressionFormProps = {
  dockingCode: string;
  products: CargoSuppressionPrefillProductOption[];
  generalCargoErrors: string[];
  containerOptions: CargoSuppressionPrefillContainerOption[];
  hasContainerOperation: boolean;
  hasGeneralCargoOperation: boolean;
  editingItem?: CargoSuppressionItemType | null;
  onSaved: () => void;
  onCancel: () => void;
};

export function CargoSuppressionForm(props: CargoSuppressionFormProps) {
  const {
    dockingCode,
    products,
    generalCargoErrors,
    containerOptions,
    hasContainerOperation,
    hasGeneralCargoOperation,
    editingItem,
    onSaved,
    onCancel,
  } = props;

  const [form] = Form.useForm();
  const initialTipo: 'CONTAINER' | 'GENERAL_CARGO' = hasContainerOperation
    ? 'CONTAINER'
    : 'GENERAL_CARGO';
  const [tipo, setTipo] = useState<'CONTAINER' | 'GENERAL_CARGO'>(initialTipo);

  const isEdit = !!editingItem;

  const [createSuppression, { isLoading: isCreating }] =
    useCreateCargoSuppressionInternalMutation();
  const [updateSuppression, { isLoading: isUpdating }] =
    useUpdateCargoSuppressionInternalMutation();

  useEffect(() => {
    if (editingItem) {
      const editTipo =
        editingItem.ncmsh2 === 'CT' ? 'CONTAINER' : 'GENERAL_CARGO';
      setTipo(editTipo);
      form.setFieldsValue({
        tipo: editTipo,
        motivo: editingItem.reason,
        id_conteiner: editingItem.id_conteiner ?? undefined,
        cheio_vazio: editingItem.full_or_empty ?? undefined,
        peso: editingItem.weight ? Number(editingItem.weight) : undefined,
      });
    } else {
      form.resetFields();
      setTipo(initialTipo);
      form.setFieldsValue({ tipo: initialTipo });
    }
  }, [editingItem, initialTipo]);

  const validProducts = products.filter(
    (p) => p.cargo_id && p.ncmsh2 && p.ncmsh4
  );

  async function handleFinish(values: any) {
    if (isEdit && editingItem) {
      const patch: CargoSuppressionInternalPatchPayload = {
        reason: values.motivo,
      };
      if (editingItem.ncmsh2 === 'CT') {
        patch.full_or_empty = values.cheio_vazio;
      } else {
        patch.weight = formatPesoForBackend(values.peso);
      }
      const res = await updateSuppression({
        docking_code: dockingCode,
        suppression_id: editingItem.id,
        payload: patch,
      });
      if ('data' in res) {
        message.success('Supressão atualizada');
        onSaved();
      }
      return;
    }

    const payload: CargoSuppressionInternalCreatePayload = {
      type: values.tipo,
      reason: values.motivo,
    };
    if (values.tipo === 'CONTAINER') {
      payload.id_conteiner = values.id_conteiner;
      payload.full_or_empty = values.cheio_vazio;
    } else {
      payload.product_id = values.product_id;
      payload.weight = formatPesoForBackend(values.peso);
    }
    const res = await createSuppression({
      docking_code: dockingCode,
      payload,
    });
    if ('data' in res) {
      message.success('Supressão registrada');
      onSaved();
    }
  }

  const showGeneralCargoBlocker =
    !isEdit && tipo === 'GENERAL_CARGO' && generalCargoErrors.length > 0;

  const containerSelectOptions = containerOptions.map((c) => ({
    id: c.code,
    name: `${c.code}${
      c.is_full === true ? ' (Cheio)' : c.is_full === false ? ' (Vazio)' : ''
    }`,
  }));

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ tipo: initialTipo }}
    >
      <Form.Item label="Tipo de supressão" name="tipo">
        <Radio.Group
          disabled={isEdit}
          onChange={(e) => setTipo(e.target.value)}
          buttonStyle="solid"
          value={tipo}
        >
          <Radio.Button
            value="CONTAINER"
            disabled={!hasContainerOperation && !isEdit}
          >
            Contêiner
          </Radio.Button>
          <Radio.Button
            value="GENERAL_CARGO"
            disabled={!hasGeneralCargoOperation && !isEdit}
          >
            Demais Cargas
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      {tipo === 'CONTAINER' ? (
        <Row gutter={16}>
          <FormItemSelect
            colSpan={12}
            name="id_conteiner"
            label="Contêiner"
            disabled={isEdit}
            allowClear
            showSearch
            filterOption={(input, option) =>
              String(option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              containerSelectOptions.length === 0
                ? 'Nenhum contêiner cadastrado para esta atracação'
                : undefined
            }
            dataList={containerSelectOptions}
            rules={[{ required: true, message: 'Informe o contêiner' }]}
          />
          <Form.Item
            name="cheio_vazio"
            label="Estado"
            rules={[{ required: true, message: 'Informe o estado' }]}
            style={{ width: 200 }}
          >
            <Radio.Group>
              <Radio value="C">Cheio</Radio>
              <Radio value="V">Vazio</Radio>
            </Radio.Group>
          </Form.Item>
        </Row>
      ) : (
        <>
          {showGeneralCargoBlocker && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Pré-condições para supressão de cargas"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                  {generalCargoErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              }
            />
          )}
          <Row gutter={16}>
            <FormItemSelect
              colSpan={16}
              name="product_id"
              label="Mercadoria"
              disabled={isEdit || showGeneralCargoBlocker}
              allowClear
              showSearch
              notFoundContent="Nenhuma mercadoria com NCM cadastrado"
              dataList={validProducts.map((p) => ({
                id: p.product_id,
                name: `${p.cargo_name} — NCM ${p.ncm_raw}`,
              }))}
              rules={[{ required: !isEdit, message: 'Selecione a mercadoria' }]}
            />
            <FormItemInputNumber
              colSpan={8}
              name="peso"
              label="Peso (ton)"
              rules={[{ required: true, message: 'Informe o peso' }]}
            />
          </Row>
        </>
      )}

      <Form.Item
        name="motivo"
        label={`Motivo da supressão (${MOTIVO_MIN}–${MOTIVO_MAX} caracteres)`}
        rules={[
          { required: true, message: 'Informe o motivo' },
          {
            min: MOTIVO_MIN,
            message: `Mínimo ${MOTIVO_MIN} caracteres`,
          },
          {
            max: MOTIVO_MAX,
            message: `Máximo ${MOTIVO_MAX} caracteres`,
          },
        ]}
      >
        <Input.TextArea
          rows={3}
          showCount
          maxLength={MOTIVO_MAX}
          placeholder="Descreva o motivo da omissão de escala"
        />
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={isCreating || isUpdating}
          disabled={showGeneralCargoBlocker}
        >
          {isEdit ? 'Salvar alterações' : 'Registrar supressão'}
        </Button>
      </div>
    </Form>
  );
}
